import React from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';

import BackofficeReportsApi from './backofficeReportsApi';

const MAX_EXPORT_DAYS = 45;
const BUSINESS_TIMEZONE = 'Europe/Bucharest';

function localIsoDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function businessToday() {
  try {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: BUSINESS_TIMEZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(new Date());
    const values = parts.reduce((result, part) => ({
      ...result,
      [part.type]: part.value,
    }), {});
    return `${values.year}-${values.month}-${values.day}`;
  } catch (error) {
    return localIsoDate(new Date());
  }
}

function shiftIsoDate(value, days) {
  const date = new Date(`${value}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().substring(0, 10);
}

function inclusiveDays(dateFrom, dateTo) {
  const start = Date.parse(`${dateFrom}T00:00:00Z`);
  const end = Date.parse(`${dateTo}T00:00:00Z`);

  if (Number.isNaN(start) || Number.isNaN(end)) return 0;
  return Math.round((end - start) / 86400000) + 1;
}

function rangeForPreset(preset) {
  const businessDate = businessToday();
  const dateTo = preset === 'yesterday' || preset === 'last_7d' || preset === 'last_30d'
    ? shiftIsoDate(businessDate, -1)
    : businessDate;

  if (preset === 'today' || preset === 'yesterday') {
    return { dateFrom: dateTo, dateTo };
  }

  if (preset === 'this_month') {
    return {
      dateFrom: `${dateTo.substring(0, 7)}-01`,
      dateTo,
    };
  }

  if (preset === 'last_month') {
    const [year, month] = dateTo.split('-').map(Number);
    const lastDay = new Date(Date.UTC(year, month - 1, 0));
    const firstDay = new Date(Date.UTC(
      lastDay.getUTCFullYear(),
      lastDay.getUTCMonth(),
      1,
    ));
    return {
      dateFrom: firstDay.toISOString().substring(0, 10),
      dateTo: lastDay.toISOString().substring(0, 10),
    };
  }

  const days = preset === 'last_7d' ? 7 : 30;
  return {
    dateFrom: shiftIsoDate(dateTo, -(days - 1)),
    dateTo,
  };
}

function defaultRange(periodMode, dateFrom, dateTo) {
  if (periodMode === 'custom' && dateFrom && dateTo) {
    return { dateFrom, dateTo, rangePreset: 'custom' };
  }
  if (periodMode === 'today' || periodMode === 'yesterday') {
    return {
      ...rangeForPreset(periodMode),
      rangePreset: periodMode,
    };
  }

  const rangePreset = periodMode === 'last_7d' ? 'last_7d' : 'last_30d';
  return {
    ...rangeForPreset(rangePreset),
    rangePreset,
  };
}

function csvValue(value) {
  if (value === null || typeof value === 'undefined') return '""';
  const stringValue = String(value);
  const trimmedValue = stringValue.trimStart();
  const numericValue = /^-?\d+(?:\.\d+)?%?$/.test(trimmedValue.trim());
  const safeValue = !numericValue && /^[=+\-@\t\r]/.test(trimmedValue)
    ? `'${stringValue}`
    : stringValue;
  return `"${safeValue.replace(/"/g, '""')}"`;
}

function requestErrorMessage(error) {
  const status = Number(error && error.status);
  const response = error && error.responseJSON;

  if (status === 401) return 'Your session has expired. Sign in again and retry.';
  if (status === 403) return 'You do not have permission to export this report.';
  if (status === 404) return 'The detailed Clients Activity export is not available on this backend.';
  if (status === 400) {
    const dateError = response && (response.dateTo || response.dateFrom);
    return dateError ? String(dateError) : 'The export dates are not valid.';
  }
  if (status >= 500) return 'The server could not prepare the export. Please retry.';
  return 'The export could not be loaded. Check the connection and retry.';
}

class ClientsActivityDetailedExport extends React.Component {
  constructor(props) {
    super(props);

    const range = defaultRange(props.periodMode, props.dateFrom, props.dateTo);
    this.state = {
      open: false,
      dateFrom: range.dateFrom,
      dateTo: range.dateTo,
      rangePreset: range.rangePreset,
      loading: false,
      error: '',
      notice: '',
      success: '',
    };
    this.requestId = 0;
  }

  componentWillUnmount() {
    this.requestId += 1;
  }

  handleOpen = () => {
    const range = defaultRange(this.props.periodMode, this.props.dateFrom, this.props.dateTo);
    this.setState({
      open: true,
      dateFrom: range.dateFrom,
      dateTo: range.dateTo,
      rangePreset: range.rangePreset,
      error: '',
      notice: '',
      success: '',
    });
  };

  handleClose = () => {
    if (this.state.loading) return;
    this.setState({ open: false, error: '', notice: '', success: '' });
  };

  handleDateChange = (field) => (event) => {
    const value = event.target.value;
    this.setState((previousState) => {
      const dates = { [field]: value };

      if (field === 'dateFrom' && value && previousState.dateTo && value > previousState.dateTo) {
        dates.dateTo = value;
      }
      if (field === 'dateTo' && value && previousState.dateFrom && value < previousState.dateFrom) {
        dates.dateFrom = value;
      }

      return {
        ...dates,
        rangePreset: 'custom',
        error: '',
        notice: '',
        success: '',
      };
    });
  };

  handlePresetChange = (event, rangePreset) => {
    if (!rangePreset) return;
    if (rangePreset === 'custom') {
      this.setState({
        rangePreset,
        error: '',
        notice: '',
        success: '',
      });
      return;
    }

    this.setState({
      ...rangeForPreset(rangePreset),
      rangePreset,
      error: '',
      notice: '',
      success: '',
    });
  };

  validateDates = () => {
    const { dateFrom, dateTo } = this.state;
    const days = inclusiveDays(dateFrom, dateTo);

    if (!dateFrom || !dateTo || days < 1) {
      return 'Select a valid start and end date.';
    }
    if (days > MAX_EXPORT_DAYS) {
      return `Choose no more than ${MAX_EXPORT_DAYS} inclusive days.`;
    }
    if (dateTo > businessToday()) {
      return 'The end date cannot be in the future.';
    }
    return '';
  };

  buildCsv = (response) => {
    const meta = response.meta || {};
    const summary = response.summary || {};
    const rows = Array.isArray(response.rows) ? response.rows : [];
    const currency = meta.currency || 'RON';
    const headers = [
      { key: 'date', label: 'Date' },
      { key: 'location_iref', label: 'Location ID' },
      { key: 'location_name', label: 'Restaurant location' },
      { key: 'registered_orders', label: 'Registered orders' },
      { key: 'anonymous_orders', label: 'Anonymous orders' },
      { key: 'total_orders', label: 'Total orders' },
      { key: 'registered_value', label: `Registered sales (${currency})` },
      { key: 'anonymous_value', label: `Anonymous sales (${currency})` },
      { key: 'total_value', label: `Total sales (${currency})` },
      { key: 'registered_order_share', label: 'Registered order share %' },
      { key: 'anonymous_order_share', label: 'Anonymous order share %' },
      { key: 'registered_value_share', label: 'Registered sales share %' },
      { key: 'anonymous_value_share', label: 'Anonymous sales share %' },
      { key: 'registered_aov', label: `Registered AOV (${currency})` },
      { key: 'anonymous_aov', label: `Anonymous AOV (${currency})` },
      { key: 'total_aov', label: `Total AOV (${currency})` },
    ];
    const moneyKeys = new Set([
      'registered_value',
      'anonymous_value',
      'total_value',
      'registered_aov',
      'anonymous_aov',
      'total_aov',
    ]);
    const percentKeys = new Set([
      'registered_order_share',
      'anonymous_order_share',
      'registered_value_share',
      'anonymous_value_share',
    ]);

    const csvRows = [
      ['Daily client activity by restaurant'].map(csvValue).join(','),
      ['Date from', meta.dateFrom || this.state.dateFrom].map(csvValue).join(','),
      ['Date to', meta.dateTo || this.state.dateTo].map(csvValue).join(','),
      ['Timezone', meta.timezone || BUSINESS_TIMEZONE].map(csvValue).join(','),
      ['Currency', currency].map(csvValue).join(','),
      ['Sales location', this.props.locationName || 'All locations'].map(csvValue).join(','),
      ['Location basis', meta.locationBasis || 'Restaurant/POS where the sale occurred'].map(csvValue).join(','),
      ['Unknown location', meta.unknownLocationBucket || 'Other / unknown'].map(csvValue).join(','),
      ['Contains client identity', meta.containsClientIdentity ? 'Yes' : 'No'].map(csvValue).join(','),
      ['Generated at', meta.generatedAt || new Date().toISOString()].map(csvValue).join(','),
      '',
      ['Summary'].map(csvValue).join(','),
      ['Registered orders', summary.registered_orders || 0].map(csvValue).join(','),
      ['Anonymous orders', summary.anonymous_orders || 0].map(csvValue).join(','),
      ['Total orders', summary.total_orders || 0].map(csvValue).join(','),
      [`Registered sales (${currency})`, Number(summary.registered_value || 0).toFixed(2)].map(csvValue).join(','),
      [`Anonymous sales (${currency})`, Number(summary.anonymous_value || 0).toFixed(2)].map(csvValue).join(','),
      [`Total sales (${currency})`, Number(summary.total_value || 0).toFixed(2)].map(csvValue).join(','),
      ['Registered order share %', `${Number(summary.registered_order_share || 0).toFixed(2)}%`].map(csvValue).join(','),
      ['Anonymous order share %', `${Number(summary.anonymous_order_share || 0).toFixed(2)}%`].map(csvValue).join(','),
      ['Registered sales share %', `${Number(summary.registered_value_share || 0).toFixed(2)}%`].map(csvValue).join(','),
      ['Anonymous sales share %', `${Number(summary.anonymous_value_share || 0).toFixed(2)}%`].map(csvValue).join(','),
      [`Registered AOV (${currency})`, Number(summary.registered_aov || 0).toFixed(2)].map(csvValue).join(','),
      [`Anonymous AOV (${currency})`, Number(summary.anonymous_aov || 0).toFixed(2)].map(csvValue).join(','),
      [`Total AOV (${currency})`, Number(summary.total_aov || 0).toFixed(2)].map(csvValue).join(','),
      '',
      headers.map((header) => csvValue(header.label)).join(','),
      ...rows.map((row) => headers.map((header) => {
        if (moneyKeys.has(header.key)) {
          return csvValue(Number(row[header.key] || 0).toFixed(2));
        }
        if (percentKeys.has(header.key)) {
          return csvValue(`${Number(row[header.key] || 0).toFixed(2)}%`);
        }
        return csvValue(row[header.key]);
      }).join(',')),
    ];

    return csvRows.join('\n');
  };

  downloadCsv = (response) => {
    const content = this.buildCsv(response);
    const blob = new Blob([`\uFEFF${content}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.setAttribute(
      'download',
      `client_activity_daily_location_${this.state.dateFrom}_${this.state.dateTo}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  handleExport = () => {
    if (this.state.loading) return;

    const validationError = this.validateDates();
    if (validationError) {
      this.setState({ error: validationError, notice: '', success: '' });
      return;
    }

    const requestId = ++this.requestId;
    this.setState({ loading: true, error: '', notice: '', success: '' });

    BackofficeReportsApi.getClientsRegisteredActivityDetailedExportAsync(
      this.props.client,
      {
        dateFrom: this.state.dateFrom,
        dateTo: this.state.dateTo,
        location: this.props.location || '0',
      }
    ).then((response) => {
      if (requestId !== this.requestId) return;
      if (!response || !Array.isArray(response.rows) || response.rows.length === 0) {
        this.setState({
          loading: false,
          notice: 'No matching client activity was found. No file was downloaded.',
        });
        return;
      }
      this.downloadCsv(response);
      this.setState({
        loading: false,
        success: 'CSV downloaded. It contains aggregate rows only.',
      });
    }).catch((error) => {
      if (requestId !== this.requestId) return;
      this.setState({
        loading: false,
        error: requestErrorMessage(error),
      });
    });
  };

  render() {
    const {
      open,
      dateFrom,
      dateTo,
      rangePreset,
      loading,
      error,
      notice,
      success,
    } = this.state;
    const days = inclusiveDays(dateFrom, dateTo);
    const today = businessToday();

    return (
      <>
        <Button
          variant="outlined"
          size="small"
          startIcon={<DownloadRoundedIcon />}
          onClick={this.handleOpen}
          sx={{
            flexShrink: 0,
            borderRadius: '10px',
            textTransform: 'none',
            fontWeight: 800,
            whiteSpace: 'nowrap',
          }}
        >
          Detailed CSV
        </Button>

        <Dialog open={open} onClose={this.handleClose} fullWidth maxWidth="sm">
          <DialogTitle sx={{ fontWeight: 800 }}>Detailed Clients Activity CSV</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 0.5 }}>
              <Typography color="text.secondary" variant="body2">
                These dates apply only to this export. Maximum {MAX_EXPORT_DAYS} inclusive days;
                the current sales-location filter applies and client identity is not included.
              </Typography>

              <ToggleButtonGroup
                exclusive
                size="small"
                value={rangePreset}
                onChange={this.handlePresetChange}
                aria-label="Export period presets"
                disabled={loading}
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 0.75,
                  '& .MuiToggleButtonGroup-grouped': {
                    m: 0,
                    px: 1.25,
                    py: 0.5,
                    border: '1px solid rgba(15, 23, 42, 0.14) !important',
                    borderRadius: '9px !important',
                    fontSize: '0.74rem',
                    fontWeight: 700,
                    textTransform: 'none',
                  },
                }}
              >
                <ToggleButton value="today" title="Today (in progress)">Today</ToggleButton>
                <ToggleButton value="yesterday" aria-label="Yesterday" title="Yesterday">YDay</ToggleButton>
                <ToggleButton value="last_7d">Last 7 complete days</ToggleButton>
                <ToggleButton value="last_30d">Last 30 complete days</ToggleButton>
                <ToggleButton value="this_month">This month</ToggleButton>
                <ToggleButton value="last_month">Last month</ToggleButton>
                <ToggleButton value="custom">Custom</ToggleButton>
              </ToggleButtonGroup>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <TextField
                  fullWidth
                  type="date"
                  label="Date from"
                  value={dateFrom}
                  onChange={this.handleDateChange('dateFrom')}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ readOnly: rangePreset !== 'custom' }}
                  inputProps={{ max: today }}
                  disabled={loading}
                />
                <TextField
                  fullWidth
                  type="date"
                  label="Date to"
                  value={dateTo}
                  onChange={this.handleDateChange('dateTo')}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ readOnly: rangePreset !== 'custom' }}
                  inputProps={{ max: today }}
                  disabled={loading}
                />
              </Stack>

              <Typography variant="caption" color={days > MAX_EXPORT_DAYS ? 'error' : 'text.secondary'}>
                {days > 0 ? `${days} inclusive day${days === 1 ? '' : 's'} selected` : 'Select both dates'}
                {rangePreset !== 'custom' ? ' · Select Custom to edit dates.' : ''}
              </Typography>

              {error && <Alert severity="error">{error}</Alert>}
              {notice && <Alert severity="info">{notice}</Alert>}
              {success && <Alert severity="success">{success}</Alert>}

              <Box
                sx={{
                  p: 1.5,
                  borderRadius: '12px',
                  bgcolor: '#f8fafc',
                  border: '1px solid rgba(15, 23, 42, 0.08)',
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 800 }}>
                  Daily activity by restaurant
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  One aggregate row per date and restaurant, split into registered and anonymous activity.
                </Typography>
                <Button
                  fullWidth
                  sx={{ mt: 1, textTransform: 'none', fontWeight: 800 }}
                  variant="contained"
                  disabled={loading}
                  onClick={this.handleExport}
                  startIcon={loading
                    ? <CircularProgress size={16} color="inherit" />
                    : <DownloadRoundedIcon />}
                >
                  Export daily activity by restaurant
                </Button>
              </Box>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={this.handleClose} disabled={loading} sx={{ textTransform: 'none' }}>
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }
}

export default ClientsActivityDetailedExport;
